import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import Reveal from "../components/Reveal";

function PageHero({ overline, title, text, image }) {
    return (
        <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden" data-testid="page-hero">
            {image && (
                <>
                    <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
                </>
            )}
            <div className="container-nm relative">
                <Reveal>
                    <div className="kicker mb-6">{overline}</div>
                </Reveal>
                <Reveal delay={100}>
                    <h1 className="font-display text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[5.5rem] font-extrabold leading-[0.95] tracking-[-0.035em]">
                        {title}
                    </h1>
                </Reveal>
                {text && (
                    <Reveal delay={200}>
                        <p className="mt-8 text-lg md:text-xl text-white/75 max-w-3xl leading-relaxed">
                            {text}
                        </p>
                    </Reveal>
                )}
            </div>
        </section>
    );
}

function ArchiveGrid({ items, basePath, testIdPrefix }) {
    if (!items.length) {
        return (
            <div className="container-nm py-20 text-white/60" data-testid="archive-empty">
                Nessun contenuto pubblicato al momento.
            </div>
        );
    }
    return (
        <section className="container-nm py-16 md:py-24" data-testid={`archive-${testIdPrefix}`}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
                {items.map((it, i) => {
                    const span = i % 5 === 0 ? "md:col-span-8" : "md:col-span-4";
                    const aspect = i % 5 === 0 ? "aspect-[16/10]" : "aspect-[4/5]";
                    return (
                        <Reveal delay={(i % 3) * 120} key={it.id} className={span}>
                            <Link
                                to={`${basePath}/${it.slug}`}
                                className="editorial-card group block text-white"
                                data-testid={`${testIdPrefix}-card-${it.slug}`}
                            >
                                <div className={`img-wrap ${aspect} bg-neutral-900`}>
                                    {it.cover_image && (
                                        <img
                                            src={mediaUrl(it.cover_image)}
                                            alt={it.title}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    )}
                                </div>
                                <div className="pt-5">
                                    <div className="kicker mb-2">
                                        {it.category || it.broadcaster || "N&M"}
                                        {it.year ? ` · ${it.year}` : ""}
                                    </div>
                                    <h3 className="font-display text-2xl md:text-3xl font-bold leading-[1.05] tracking-tight group-hover:text-[color:var(--brand-blue)] transition-colors">
                                        {it.title}
                                    </h3>
                                    {it.subtitle && (
                                        <p className="mt-2 text-white/70 text-base">{it.subtitle}</p>
                                    )}
                                </div>
                            </Link>
                        </Reveal>
                    );
                })}
            </div>
        </section>
    );
}

function useArchive(kind) {
    const [items, setItems] = useState([]);
    useEffect(() => {
        api.get(`/${kind}`).then((r) => setItems(r.data)).catch(() => setItems([]));
    }, [kind]);
    return items;
}

export function ProductionsPage() {
    const items = useArchive("productions");
    return (
        <div data-testid="productions-page">
            <PageHero
                overline="Produzioni TV"
                title="Dall'idea alla messa in onda"
                text="Siamo specializzati nella creazione e produzione di format televisivi di intrattenimento, sviluppati per il mercato broadcast e digitale. Seguiamo ogni fase: dall'ideazione alla messa in onda."
                image="https://images.unsplash.com/photo-1606920669741-c8ba74262a94?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000"
            />
            <section className="container-nm pt-4 pb-12" data-testid="productions-services">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 border-y border-white/10 py-10">
                    {["Sviluppo format", "Produzione esecutiva", "Casting", "Distribuzione"].map((s, i) => (
                        <Reveal key={s} delay={i * 100}>
                            <div className="flex items-start gap-4">
                                <div className="font-display text-xl font-bold text-[color:var(--brand-blue)]">
                                    0{i + 1}
                                </div>
                                <div className="font-display text-lg font-semibold">{s}</div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>
            <ArchiveGrid items={items} basePath="/produzioni-tv" testIdPrefix="productions" />
            <CtaBand
                title="Format originali, scalabili e monetizzabili"
                buttons={[
                    ["/contatti?kind=format", "Proponi un format"],
                    ["/contatti?kind=partner", "Collabora con noi"],
                ]}
            />
        </div>
    );
}

export function FormatsPage() {
    const items = useArchive("formats");
    return (
        <div data-testid="formats-page">
            <PageHero
                overline="Format TV"
                title="Format televisivi"
                text="Sviluppiamo format originali di intrattenimento pensati per il pubblico televisivo e digitale, progettati per essere scalabili e riconoscibili."
                image="https://images.unsplash.com/photo-1774988353278-b01caf489c4e?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000"
            />
            <ArchiveGrid items={items} basePath="/format-tv" testIdPrefix="formats" />
            <section className="bg-[#F9F8F6] text-black py-24" data-testid="formats-strategy">
                <div className="container-nm grid grid-cols-1 md:grid-cols-12 gap-10">
                    <Reveal className="md:col-span-5">
                        <div className="kicker mb-5">Strategia</div>
                        <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-[-0.03em]">
                            Multi-piattaforma per design
                        </h2>
                    </Reveal>
                    <Reveal delay={120} className="md:col-span-6 md:col-start-7 flex items-end">
                        <p className="text-lg text-neutral-700 leading-relaxed">
                            I nostri format sono pensati per essere sviluppati su più piattaforme e integrati
                            con brand e sponsor. Un'architettura editoriale flessibile che abilita nuove
                            stagioni, spin-off, branded content e attivazioni live.
                        </p>
                    </Reveal>
                </div>
            </section>
            <CtaBand
                title="Hai un'idea? Portiamola in onda insieme."
                buttons={[
                    ["/contatti?kind=format", "Proponi un format"],
                    ["/contatti?kind=partner", "Collabora con noi"],
                ]}
            />
        </div>
    );
}

export function EventsPage() {
    const items = useArchive("events");
    return (
        <div data-testid="events-page">
            <PageHero
                overline="Eventi"
                title="Eventi a 360°"
                text="Organizziamo eventi a 360°, sia per nostri format che per clienti esterni. Dal concept alla gestione operativa, per eventi corporate, branded e legati a produzioni televisive."
                image="https://images.unsplash.com/photo-1768586471756-8237f5df0dc7?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000"
            />
            <section className="container-nm pt-4 pb-10" data-testid="events-types">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-y border-white/10 py-10">
                    {[
                        ["Corporate", "Eventi istituzionali e brand experience"],
                        ["Branded", "Attivazioni con sponsor e media partner"],
                        ["TV", "Eventi legati alle nostre produzioni"],
                    ].map(([t, d], i) => (
                        <Reveal key={t} delay={i * 100}>
                            <div>
                                <div className="kicker mb-3">0{i + 1}</div>
                                <div className="font-display text-2xl font-bold mb-2">{t}</div>
                                <div className="text-white/60">{d}</div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>
            <ArchiveGrid items={items} basePath="/eventi" testIdPrefix="events" />
            <CtaBand
                title="Concept → produzione → gestione"
                buttons={[
                    ["/contatti?kind=event", "Richiedi un preventivo"],
                    ["/contatti?kind=event", "Organizza il tuo evento"],
                ]}
            />
        </div>
    );
}

export function CommunicationPage() {
    return (
        <div data-testid="communication-page">
            <PageHero
                overline="Comunicazione"
                title="Strategie integrate"
                text="Sviluppiamo strategie di comunicazione integrate con contenuti ed eventi: concept creativo, posizionamento, contenuti, attivazioni."
                image="https://images.unsplash.com/photo-1603074256023-44405c02a0c3?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000"
            />
            <section className="container-nm py-20 grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                    ["Concept creativo", "Ideazione di narrative distintive, pensate per TV, digitale e live."],
                    ["Posizionamento", "Analisi del brand, audience, competitive landscape."],
                    ["Contenuti", "Produzione video, editoriale e social ad alto livello qualitativo."],
                    ["Attivazioni", "Eventi, integrazioni di brand, media plan e amplificazione."],
                ].map(([t, d], i) => (
                    <Reveal delay={i * 120} key={t}>
                        <div className="border border-white/15 p-8 h-full hover:border-[color:var(--brand-blue)] transition-colors duration-500">
                            <div className="kicker mb-4">0{i + 1}</div>
                            <h3 className="font-display text-3xl font-bold mb-3">{t}</h3>
                            <p className="text-white/70">{d}</p>
                        </div>
                    </Reveal>
                ))}
            </section>
            <CtaBand title="Costruiamo la tua prossima narrazione" buttons={[["/contatti", "Richiedi informazioni"]]} />
        </div>
    );
}

export function CastingPage() {
    return (
        <div data-testid="casting-page">
            <PageHero
                overline="Casting"
                title="Casting per i nostri format"
                text="Gestiamo casting esclusivamente per i nostri format televisivi. Non realizziamo casting per terzi."
                image="https://images.unsplash.com/photo-1653640869615-e9878a2c8344?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000"
            />
            <section className="container-nm py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
                <Reveal className="md:col-span-6">
                    <div className="border border-white/15 p-8 md:p-10">
                        <div className="kicker mb-5">Come funziona</div>
                        <ol className="space-y-5 text-white/80 list-decimal list-inside">
                            <li>Compila la candidatura via modulo contatti indicando il format di riferimento.</li>
                            <li>Il team di casting valuterà profilo e materiali inviati.</li>
                            <li>Verrai ricontattato per un eventuale provino.</li>
                        </ol>
                    </div>
                </Reveal>
                <Reveal delay={150} className="md:col-span-6">
                    <div className="bg-[color:var(--brand-blue)] text-white p-8 md:p-10 h-full flex flex-col justify-between">
                        <div>
                            <div className="kicker text-white/80 mb-5">Vuoi candidarti?</div>
                            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                                Partecipa ai casting di N&M Management
                            </h3>
                        </div>
                        <Link to="/contatti?kind=casting" className="btn-ghost text-white mt-10 self-start" data-testid="casting-cta">
                            Partecipa ai casting
                        </Link>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}

function CtaBand({ title, buttons = [] }) {
    return (
        <section className="bg-[#F9F8F6] text-black py-20 md:py-28" data-testid="cta-band">
            <div className="container-nm grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                <Reveal className="md:col-span-7">
                    <h3 className="font-display text-4xl md:text-5xl font-extrabold tracking-[-0.03em] leading-[0.95]">
                        {title}
                    </h3>
                </Reveal>
                <Reveal delay={120} className="md:col-span-5 flex flex-wrap gap-4">
                    {buttons.map(([to, label]) => (
                        <Link key={label} to={to} className="btn-ghost text-black">
                            {label}
                        </Link>
                    ))}
                </Reveal>
            </div>
        </section>
    );
}

export { CtaBand };
